import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { isNil, isString } from 'lodash-es';

export interface ValidationConfig {
  validators: ValidatorFn[];
  errors: ValidationErrors;
}

export interface CustomValidator {
  validator: ValidatorFn;
  errors: ValidationErrors;
}

enum FormErrorEnum {
  REQUIRED = 'required',
  REQUIRED_TRUE = 'requiredTrue',
  MAX_LENGTH = 'maxlength',
  PATTERN = 'pattern',
  CHANGED = 'changed',
  JSON = 'json',
}

export const CustomValidators = {
  [FormErrorEnum.MAX_LENGTH]: (length: number, errorText?: string) => ({
    validator: Validators.maxLength(length),
    errors: {
      ...(errorText
        ? { [FormErrorEnum.MAX_LENGTH]: errorText }
        : {
            [FormErrorEnum.MAX_LENGTH]: $localize`:@@formInputMaxLengthValidator:Length should not exceed ${length} characters`,
          }),
    },
  }),
  [FormErrorEnum.REQUIRED]: (errorText?: string) => ({
    validator: requiredValidator(),
    errors: {
      ...(errorText ? { [FormErrorEnum.REQUIRED]: errorText } : {}),
    },
  }),
  [FormErrorEnum.REQUIRED_TRUE]: (errorText?: string) => ({
    validator: Validators.requiredTrue,
    errors: {
      ...(errorText ? { [FormErrorEnum.REQUIRED_TRUE]: errorText } : {}),
    },
  }),
  [FormErrorEnum.PATTERN]: (pattern: string, errorText?: string) => ({
    validator: Validators.pattern(pattern),
    errors: {
      ...(errorText ? { [FormErrorEnum.PATTERN]: errorText } : {}),
    },
  }),
  [FormErrorEnum.CHANGED]: (value: string, errorText?: string) => ({
    validator: valueChangedValidator(value),
    errors: {
      ...(errorText ? { [FormErrorEnum.CHANGED]: errorText } : {}),
    },
  }),
  [FormErrorEnum.JSON]: (errorText?: string) => ({
    validator: jsonValidator(),
    errors: {
      ...(errorText ? { [FormErrorEnum.JSON]: errorText } : {}),
    },
  }),
};

export const getValidationConfig = (
  customValidators: CustomValidator[],
): ValidationConfig => ({
  validators: customValidators.map((validator) => validator.validator),
  errors: customValidators
    .map((validator) => validator.errors)
    .reduce((acc, curr) => ({ ...acc, ...curr })),
});

export function requiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const isOnlyWhitespace =
      isString(control.value) && control.value.trim().length === 0;
    const isValid = !isNil(control.value) && !isOnlyWhitespace;
    return isValid ? null : { required: true };
  };
}

export function valueChangedValidator(value: string): ValidatorFn {
  const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`^\\s*${escapedValue}\\s*$`);

  return (control: AbstractControl): ValidationErrors | null => {
    const newValue = control.value?.value ?? control.value;
    const valid = !regex.test(newValue);
    return valid ? null : { changed: { value: newValue } };
  };
}

export function jsonValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value.trim();

    if (!value) {
      return null;
    }

    try {
      JSON.parse(value);
      return null;
    } catch (error) {
      return {
        invalidJson: true,
      };
    }
  };
}
